import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email.trim(), password);
      nav(u?.must_change_password ? "/change-password" : "/dashboard", { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-stretch">
      {/* Left brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between p-12 w-[42%]"
        style={{ background: "var(--caa-teal-deep)", color: "#fff" }}
      >
        <div className="flex items-baseline gap-1 leading-none">
          <span className="text-2xl font-extrabold tracking-tight text-white">CRANE</span>
          <span
            className="text-2xl font-extrabold tracking-tight"
            style={{ color: "#E6F4F1" }}
          >
            AUCTIONS
          </span>
        </div>
        <div className="max-w-md space-y-4">
          <h1 className="text-4xl font-extrabold leading-tight text-white">
            Every crane sale, tracked. Every valuation, comparable.
          </h1>
          <p className="text-lg text-[color:rgba(255,255,255,0.85)]">
            Pickles, Slattery, Ross &amp; Ritchie Bros — one live database, one instant valuation
            engine.
          </p>
        </div>
        <div className="text-xs text-[color:rgba(255,255,255,0.65)]">
          © {new Date().getFullYear()} Crane Auctions Australia
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <div className="mb-6 flex justify-center">
            <Logo height={56} />
          </div>
          <h2 className="text-2xl font-bold text-[color:var(--caa-charcoal)] mb-1 text-center">
            Sign in
          </h2>
          <p className="text-sm text-[color:var(--caa-muted)] mb-8 text-center">
            Welcome back — enter your credentials to continue.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold text-[color:var(--caa-charcoal)] uppercase tracking-wider">
                Email
              </label>
              <input
                data-testid="login-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl border caa-hairline bg-white px-4 py-3 text-[15px] outline-none focus:border-[color:var(--caa-teal-deep)] focus:ring-2 focus:ring-[color:var(--caa-teal-soft)]"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[color:var(--caa-charcoal)] uppercase tracking-wider">
                Password
              </label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-xl border caa-hairline bg-white px-4 py-3 text-[15px] outline-none focus:border-[color:var(--caa-teal-deep)] focus:ring-2 focus:ring-[color:var(--caa-teal-soft)]"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div
                className="rounded-lg border border-[#f2c7c7] bg-[#f9eaea] px-3 py-2 text-sm text-[color:var(--caa-status-passed)]"
                data-testid="login-error"
              >
                {err}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full rounded-xl px-4 py-3 text-[15px] font-semibold text-white transition-colors disabled:opacity-70"
              style={{ background: "var(--caa-teal-deep)" }}
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
